import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useModuleRewards } from "@/hooks/useModuleRewards";
import { useCurriculumProgress } from "@/hooks/useCurriculumProgress";
import ModuleShell from "@/components/modules/ModuleShell";
import ModuleStepCard from "@/components/modules/ModuleStepCard";
import ModuleQuiz from "@/components/modules/ModuleQuiz";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { ArrowRight, Shield, Heart, Car, Home, CheckCircle2, AlertTriangle, Scale } from "lucide-react";

type Step = "intro" | "why_insurance" | "health" | "auto" | "home" | "deductible" | "underinsurance" | "quiz" | "summary";
const STEPS: Step[] = ["intro", "why_insurance", "health", "auto", "home", "deductible", "underinsurance", "quiz", "summary"];

const RiskProtectionModule = () => {
  const navigate = useNavigate();
  const { moduleGates, loading } = useCurriculumProgress();
  const { completeModule } = useModuleRewards();
  const [step, setStep] = useState<Step>("intro");
  const [healthPlan, setHealthPlan] = useState<string | null>(null);
  const [autoCoverage, setAutoCoverage] = useState<string | null>(null);
  const [homeChoice, setHomeChoice] = useState<string | null>(null);
  const [deductible, setDeductible] = useState(1000);
  const [completed, setCompleted] = useState(false);

  const gate = moduleGates.find(g => g.moduleKey === "risk_protection");
  const isLocked = !gate?.isUnlocked;
  if (loading) return null;

  const premium = Math.round(300 - (deductible - 500) * 0.12);

  return (
    <ModuleShell
      title="Risk Protection"
      stepIndex={STEPS.indexOf(step)}
      totalSteps={STEPS.length}
      isLocked={isLocked}
      lockMessage={gate?.missingPrereqs?.length ? `Complete: ${gate.missingPrereqs.join(", ")}` : "Complete Insurance & Risk Protection to unlock."}
      isCompleted={completed}
      completedMessage="You understand insurance coverage, deductible tradeoffs, and risk protection."
      stepKey={step}
    >
      {step === "intro" && (
        <ModuleStepCard
          icon={<Shield className="w-8 h-8 text-primary" />}
          title="Protect What You Build"
          body="Insurance is how you protect your financial progress from catastrophic loss. In this module, you'll choose coverage levels and understand the real tradeoffs."
          highlights={["Protection before growth", "One disaster without coverage can erase years of progress", "The right coverage = peace of mind"]}
          action={<Button onClick={() => setStep("why_insurance")} className="w-full gap-2">Let's Begin <ArrowRight className="w-4 h-4" /></Button>}
        />
      )}

      {step === "why_insurance" && (
        <ModuleStepCard
          icon={<Scale className="w-8 h-8 text-accent" />}
          title="Risk Pooling"
          body="Insurance works by pooling risk across many people. You pay a small predictable premium so that if a large unpredictable event happens, you're covered. Without it, you absorb the full cost alone."
          highlights={["Small monthly cost vs catastrophic one-time cost", "You trade certainty for protection", "Not having insurance is the biggest risk"]}
          action={<Button onClick={() => setStep("health")} className="w-full gap-2">Next: Health Coverage <ArrowRight className="w-4 h-4" /></Button>}
        />
      )}

      {step === "health" && (
        <div className="space-y-6">
          <ModuleStepCard icon={<Heart className="w-8 h-8 text-destructive" />} title="Health Insurance" body="Medical emergencies can cost tens of thousands. Choose a plan:" action={<div />} />
          <div className="grid gap-3">
            {[
              { key: "hdhp", label: "High-Deductible (HDHP)", desc: "Low premium ($180/mo), high deductible ($3,000). Best with HSA for healthy individuals." },
              { key: "ppo", label: "PPO Plan", desc: "Moderate premium ($350/mo), $1,500 deductible. More flexibility choosing doctors." },
              { key: "hmo", label: "HMO Plan", desc: "Low premium ($250/mo), $500 deductible. Must use network doctors, need referrals." },
            ].map(p => (
              <button key={p.key} className={`rounded-2xl border p-4 text-left transition-all ${healthPlan === p.key ? "border-primary bg-primary/10" : "border-border bg-card/50 hover:border-primary/30"}`} onClick={() => setHealthPlan(p.key)}>
                <p className="font-heading font-bold text-sm text-foreground">{p.label}</p>
                <p className="text-xs text-muted-foreground mt-1">{p.desc}</p>
              </button>
            ))}
          </div>
          <Button onClick={() => setStep("auto")} disabled={!healthPlan} className="w-full gap-2">Next: Auto Insurance <ArrowRight className="w-4 h-4" /></Button>
        </div>
      )}

      {step === "auto" && (
        <div className="space-y-6">
          <ModuleStepCard icon={<Car className="w-8 h-8 text-primary" />} title="Auto Insurance" body="Required by law in most states. Choose your coverage level:" action={<div />} />
          <div className="grid gap-3">
            {[
              { key: "minimum", label: "State Minimum", desc: "Cheapest ($60/mo) but barely covers other driver's damages. You're exposed." },
              { key: "standard", label: "Standard Coverage", desc: "Balanced ($120/mo). Liability + collision + comprehensive. Solid protection." },
              { key: "full", label: "Full Coverage", desc: "Premium ($200/mo). Maximum protection including uninsured motorist and rental." },
            ].map(p => (
              <button key={p.key} className={`rounded-2xl border p-4 text-left transition-all ${autoCoverage === p.key ? "border-primary bg-primary/10" : "border-border bg-card/50 hover:border-primary/30"}`} onClick={() => setAutoCoverage(p.key)}>
                <p className="font-heading font-bold text-sm text-foreground">{p.label}</p>
                <p className="text-xs text-muted-foreground mt-1">{p.desc}</p>
              </button>
            ))}
          </div>
          <Button onClick={() => setStep("home")} disabled={!autoCoverage} className="w-full gap-2">Next: Home/Renter's <ArrowRight className="w-4 h-4" /></Button>
        </div>
      )}

      {step === "home" && (
        <div className="space-y-6">
          <ModuleStepCard icon={<Home className="w-8 h-8 text-accent" />} title="Home / Renter's Insurance" body="Protects your belongings and provides liability coverage:" action={<div />} />
          <div className="grid gap-3">
            {[
              { key: "renters", label: "Renter's Insurance", desc: "$15/mo. Covers personal property and liability. Often overlooked but essential." },
              { key: "homeowners", label: "Homeowner's Insurance", desc: "$150/mo. Covers structure, belongings, liability. Required by mortgage lenders." },
            ].map(p => (
              <button key={p.key} className={`rounded-2xl border p-4 text-left transition-all ${homeChoice === p.key ? "border-primary bg-primary/10" : "border-border bg-card/50 hover:border-primary/30"}`} onClick={() => setHomeChoice(p.key)}>
                <p className="font-heading font-bold text-sm text-foreground">{p.label}</p>
                <p className="text-xs text-muted-foreground mt-1">{p.desc}</p>
              </button>
            ))}
          </div>
          <Button onClick={() => setStep("deductible")} disabled={!homeChoice} className="w-full gap-2">Next: Deductible Tradeoff <ArrowRight className="w-4 h-4" /></Button>
        </div>
      )}

      {step === "deductible" && (
        <div className="space-y-6">
          <ModuleStepCard
            icon={<Scale className="w-8 h-8 text-primary" />}
            title="Deductible vs Premium"
            body="Higher deductible = lower monthly premium, but more out-of-pocket when claims happen. Find the balance that matches your emergency fund."
            subtext={`Deductible: $${deductible.toLocaleString()} · Est. Monthly Premium: $${premium}`}
            action={<div />}
          />
          <Slider value={[deductible]} min={500} max={5000} step={250} onValueChange={([v]) => setDeductible(v)} />
          <div className="flex justify-between text-xs text-muted-foreground"><span>$500 deductible</span><span>$5,000 deductible</span></div>
          <div className="rounded-2xl border border-border bg-card/50 p-4">
            <p className="text-xs text-muted-foreground">Rule of thumb: Your deductible should never exceed your emergency fund. Can you cover ${deductible.toLocaleString()} if something happens?</p>
          </div>
          <Button onClick={() => setStep("underinsurance")} className="w-full gap-2">Next: Underinsurance Risk <ArrowRight className="w-4 h-4" /></Button>
        </div>
      )}

      {step === "underinsurance" && (
        <ModuleStepCard
          icon={<AlertTriangle className="w-8 h-8 text-destructive" />}
          title="The Underinsurance Trap"
          body="Skipping insurance to save money is one of the most dangerous financial decisions. A single uninsured event — a car accident, a house fire, a medical emergency — can cost more than years of premiums combined."
          highlights={["Underinsurance is a hidden risk", "The cost of no coverage is catastrophic", "Match protection to your actual risk level"]}
          action={<Button onClick={() => setStep("quiz")} className="w-full gap-2">Take Knowledge Check <ArrowRight className="w-4 h-4" /></Button>}
        />
      )}

      {step === "quiz" && (
        <ModuleQuiz moduleKey="risk_protection" onPass={() => { setCompleted(true); setStep("summary"); completeModule("risk_protection"); }} />
      )}

      {step === "summary" && (
        <div className="space-y-6 text-center">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto border border-primary/20">
            <CheckCircle2 className="w-8 h-8 text-primary" />
          </div>
          <h2 className="font-heading text-xl font-bold text-foreground">Risk Protection Complete</h2>
          <p className="text-sm text-muted-foreground">You've built a protection framework. Insurance isn't exciting — but it's essential.</p>
          <div className="flex flex-col gap-3 pt-2">
            <Button onClick={() => navigate("/dashboard")} className="w-full">Back to Dashboard</Button>
          </div>
        </div>
      )}
    </ModuleShell>
  );
};

export default RiskProtectionModule;
